import React from 'react';
import PropTypes from 'prop-types';

import {VelocityTransitionGroup} from 'velocity-react';
import Hammer from 'react-hammerjs';

import OEToolbox from '../../lib/tools/oe-toolbox';
//import OEEventHelper from '../../lib/tools/oe-event-helper';
import OEResizeObserver from '../../lib/components/oe-resize-observer';
import {OEMediaViewerContentType, OEMediaViewerPlaybackState, OEMediaViewerReadyState, OEMediaViewerContentSizeMode} from './oe-media-viewer-model';
import OEMediaViewerContentController from './content-controller/oe-media-viewer-content-controller';
import OEMediaViewerHtmlContentController from './content-controller/oe-media-viewer-html-content-controller';
import OEMediaViewerImageContentController from './content-controller/oe-media-viewer-image-content-controller';
import OEMediaViewerPDFContentController from './content-controller/oe-media-viewer-pdf-content-controller';
import OEMediaViewerVideoContentController from './content-controller/oe-media-viewer-video-content-controller';
import OEMediaViewerOverlayController from './oe-media-viewer-overlay-controller';

class ContentControllerAdapter {

    constructor(element, props, content, progress) {
        this.element = element;
        this.props = props;
        this.content = content;
        this.instance = null;

        this.setInstance = this.setInstance.bind(this);

        this.props.ref = this.setInstance;

        this.callbacks = {};

        this.props.onReadyStateChanged = (state) => { this.onReadyStateChanged(state); };
        this.props.onPlaybackStateChanged = (state) => { this.onPlaybackStateChanged(state); };
        this.props.onDurationChanged = (duration) => { this.onDurationChanged(duration); };
        this.props.onCurrentTimeChanged = (currentTime) => { this.onCurrentTimeChanged(currentTime); };
        this.props.onEnded = () => { this.onEnded(); };
        this.props.onContentSizeChanged = (contentSize) => { this.onContentSizeChanged(contentSize); };
        this.props.onGoToItem = (name) => { this.onGoToItem(name); };
        this.props.onShowMedia = (path) => { this.onShowMedia(path); };

        this.readyState = this.content.type === OEMediaViewerContentType.video ? OEMediaViewerReadyState.noMeta : OEMediaViewerReadyState.ready;
        this.playbackState = OEMediaViewerPlaybackState.pause;
        this.currentTime = 0;
        this.duration = 0;
        this.progress = 0;

        if(typeof(progress) === 'number')    this.setProgress(progress);

        this.contentSize = content.size;
    }

    getReadyState()  {
        return this.readyState;
    }

    getContentSize()    {
        return this.contentSize;
    }

    play()  {
        this.setPlaybackState(OEMediaViewerPlaybackState.play);
    }

    pause()  {
        this.setPlaybackState(OEMediaViewerPlaybackState.pause);
    }

    stop()  {
        this.pause();
        this.seek(0);
    }

    getPlaybackState()  {
        return this.playbackState;
    }

    setPlaybackState(playbackState)  {
        if(this.instance && typeof(this.instance.setPlaybackState) === 'function')  this.instance.setPlaybackState(playbackState);
        this.onPlaybackStateChanged(playbackState);
    }

    getCurrentTime()    {
        return this.currentTime;
    }

    getDuration()    {
        return this.duration;
    }

    setProgress(progress)   {
        let oldProgress = this.progress;

        if(this.instance && typeof(this.instance.setProgress) === 'function')  {
            this.instance.setProgress(progress);
        } else {
            this.progress = progress;
            this.progressApplied = true;
        }

        if(this.progress !== oldProgress && this.callbacks.onProgressChanged)   this.callbacks.onProgressChanged(this, this.progress);
    }

    getProgress()   {
        return typeof(this.progress) === 'number' ? this.progress : (this.getDuration() == 0 ? 0 : this.getCurrentTime()/this.getDuration());
    }

    seek(seconds)   {
        if(this.instance && typeof(this.instance.seek) === 'function')  this.instance.seek(seconds);
        this.onCurrentTimeChanged(seconds);
        this.progressApplied = false;
    }

    setInstance(instance)   {
        this.instance = instance;

        let readyState = this.readyState;
        let playbackState = this.playbackState;
        let duration = this.duration;
        let currentTime = this.currentTime;
        let contentSize = this.contentSize;

        if(this.instance)   {
            if(this.progressApplied)   {
                this.setProgress(this.progress);
                this.progressApplied = false;
            } else {
                this.seek(this.currentTime);
            }
            this.setPlaybackState(this.playbackState);

            if(typeof(this.instance.getReadyState) === 'function') readyState = this.instance.getReadyState();
            if(typeof(this.instance.getPlaybackState) === 'function') playbackState = this.instance.getPlaybackState();
            if(typeof(this.instance.getDuration) === 'function') duration = this.instance.getDuration();
            if(typeof(this.instance.getCurrentTime) === 'function') currentTime = this.instance.getCurrentTime();
            if(typeof(this.instance.getContentSize) === 'function') contentSize = this.instance.getContentSize();
        } else {
            readyState = this.content.type === OEMediaViewerContentType.video ? OEMediaViewerReadyState.noMeta : OEMediaViewerReadyState.ready;
        }

        this.onReadyStateChanged(readyState);
        this.onPlaybackStateChanged(playbackState);
        this.onDurationChanged(duration);
        this.onCurrentTimeChanged(currentTime);
        this.onContentSizeChanged(contentSize);
    }

    updateProgress()    {
        var oldProgress = this.progress;
        this.progress = this.duration == 0 ? 0 : this.currentTime/this.duration;
        if(this.progress !== oldProgress && this.callbacks.onProgressChanged)   this.callbacks.onProgressChanged(this, this.progress)
    }

    onReadyStateChanged(state) {
        if(this.readyState !== state)    {
            this.readyState = state;
            if(this.callbacks.onReadyStateChanged)  this.callbacks.onReadyStateChanged(this, state);
        }
    }

    onPlaybackStateChanged(state)   {
        if(this.playbackState !== state)    {
            this.playbackState = state;
            if(this.callbacks.onPlaybackStateChanged)   this.callbacks.onPlaybackStateChanged(this, state);
        }
    }

    onDurationChanged(duration)   {
        if(this.duration !== duration)    {
            this.duration = duration;
            this.updateProgress();
            if(this.callbacks.onDurationChanged)    this.callbacks.onDurationChanged(this, duration);
        }
    }

    onCurrentTimeChanged(currentTime)   {
        if(this.currentTime !== currentTime)    {
            this.currentTime = currentTime;
            this.updateProgress();
            if(this.callbacks.onCurrentTimeChanged) this.callbacks.onCurrentTimeChanged(this, currentTime);
        }
    }

    onEnded()   {
        if(this.callbacks.onEnded) this.callbacks.onEnded(this);
    }

    onContentSizeChanged(contentSize)  {
        let contentSize_;
        if(contentSize || this.content.size)    contentSize_ = Object.assign({}, contentSize, this.content.size);
        if(OEToolbox.shallowEqual(this.contentSize, contentSize_))    return;
        this.contentSize = contentSize_;
        if(this.callbacks.onContentSizeChanged) this.callbacks.onContentSizeChanged(this, contentSize_);
    }

    onGoToItem(name)   {
        if(this.callbacks.onGoToItem)   this.callbacks.onGoToItem(this, name);
    }

    onShowMedia(path)   {
        if(this.callbacks.onShowMedia)  this.callbacks.onShowMedia(this, path);
    }

    setCallbacks(onReadyStateChanged, onPlaybackStateChanged, onDurationChanged, onCurrentTimeChanged, onProgressChanged, onEnded, onContentSizeChanged, onGoToItem, onShowMedia)    {
        this.callbacks = {
            onReadyStateChanged: onReadyStateChanged,
            onPlaybackStateChanged: onPlaybackStateChanged,
            onDurationChanged: onDurationChanged,
            onCurrentTimeChanged: onCurrentTimeChanged,
            onProgressChanged: onProgressChanged,
            onEnded: onEnded,
            onContentSizeChanged: onContentSizeChanged,
            onGoToItem: onGoToItem,
            onShowMedia: onShowMedia
        };
    }

    clearCallbacks()    {
        this.callbacks = {};
    }

    canSwipe() {
        if(!this.instance || typeof(this.instance.canSwipe) !== 'function') return true;
        return this.instance.canSwipe();
    }
};

export default class OEMediaViewerWindowController extends React.PureComponent {

    constructor(props) {
        super(props);

        let _dataSource = null;
        let _activeContentIndex = 0;
        let _contentControllers = {};
        let _contentSize = null;
        let _contentRect = null;

        this.state = {
            activeContentIndex: 0,
            dataSource: _dataSource,
            contentControllers: [],
            activeContentController: null,
            animated: false,
            shift: 0,
            readyState: OEMediaViewerReadyState.ready,
            playbackState: OEMediaViewerPlaybackState.pause,
            duration: 0,
            time: 0
        };

        this.getDataSource = function() {
            return _dataSource;
        };

        this.setDataSource = function(dataSource, reload)  {
            if(_dataSource === dataSource)  return;
            let oldValue = _dataSource;
            _dataSource = dataSource;
            this.setState({dataSource: _dataSource});
            if((!oldValue && dataSource) || reload)   this.reload();
        };

        this.getReadyState = function()    {
            let controller = _contentControllers[_activeContentIndex.toString()];
            return controller ? controller.getReadyState() : OEMediaViewerReadyState.noMeta;
        }

        this.getContentSize = function()    {
            return _contentSize;
        }

        this.play = function()  {
            let controller = _contentControllers[_activeContentIndex.toString()];
            if(controller)  controller.play();
        };

        this.pause = function()  {
            let controller = _contentControllers[_activeContentIndex.toString()];
            if(controller)  controller.pause();
        };

        this.stop = function()  {
            let controller = _contentControllers[_activeContentIndex.toString()];
            if(controller)  controller.stop();
        };

        this.getPlaybackState = function()  {
            let controller = _contentControllers[_activeContentIndex.toString()];
            return controller ? controller.getPlaybackState() : OEMediaViewerPlaybackState.pause;
        }

        this.setPlaybackState = function(state)  {
            let controller = _contentControllers[_activeContentIndex.toString()];
            if(controller)  controller.setPlaybackState(state);
        }

        this.getCurrentTime = function()    {
            let controller = _contentControllers[_activeContentIndex.toString()];
            return controller ? controller.getCurrentTime() : 0;
        }

        this.getDuration = function()    {
            let controller = _contentControllers[_activeContentIndex.toString()];
            return controller ? controller.getDuration() : 0;
        }

        this.getProgress = function()  {
            let controller = _contentControllers[_activeContentIndex.toString()];
            return controller ? controller.getProgress() : 0;
        };

        this.setProgress = function(progress)  {
            let controller = _contentControllers[_activeContentIndex.toString()];
            if(controller)  controller.setProgress(progress);
        };

        this.seek = function(seconds)   {
            let controller = _contentControllers[_activeContentIndex.toString()];
            if(controller)  controller.seek(seconds);
        };

        this.getActiveContentIndex = function() {
            return _activeContentIndex;
        };

        this.updateCache = function()   {
            if(!this.props.isCaching)   {
                this.clearCache(this.props.preloadWidth);
            } else {
                this.removeNonCacheable(this.props.preloadWidth);
            }
            this.preload();
        };

        this.clearCache = function(distance)   {        // clears cached content controllers which are not in the interval [_activeContentIndex - distance, _activeContentIndex + distance]
            distance = typeof(distance) === 'number' ? distance : 0;
            let keys = Object.keys(_contentControllers);
            for(let i = 0; i < keys.length; ++i)  {
                let key = keys[i];
                let controller = _contentControllers[key];
                if(controller.index < _activeContentIndex - distance || controller.index > _activeContentIndex + distance)  {
                    delete _contentControllers[key];
                }
            }
            this.setState({contentControllers: Object.values(_contentControllers) });
        };

        this.removeNonCacheable = function(distance)   {
            distance = typeof(distance) === 'number' ? distance : 0;
            let keys = Object.keys(_contentControllers);
            for(let i = 0; i < keys.length; ++i)  {
                let key = keys[i];
                let controller = _contentControllers[key];
                if(controller.index < _activeContentIndex - distance || controller.index > _activeContentIndex + distance && controller.content.isChachingEnabled === false)  {
                    delete _contentControllers[key];
                }
            }
            this.setState({contentControllers: Object.values(_contentControllers) });
        };

        this.preload = function()   {
            for(let i = 1; i <= this.props.preloadWidth; ++i)   {
                this.loadContentController(_activeContentIndex - i, null, true);
                this.loadContentController(_activeContentIndex + i, null, true);
            }
        };

        this.set = function(index, animated, force, progress)    {
            if(!this.loadContentController(index, progress)) return;
            this.applyContentController(index, animated, force);
        };

        this.next = function(animated)  {
            let dataSource = _dataSource;
            if(!dataSource) return false;
            let count = dataSource.mediaViewerContentCount();
            if(!(_activeContentIndex + 1 < count || count < 0)) return false;
            animated = typeof(animated) === 'boolean' ? animated : true;
            this.set(_activeContentIndex + 1, animated);
        };

        this.previous = function(animated)  {
            let dataSource = _dataSource;
            if(!dataSource) return false;
            let count = dataSource.mediaViewerContentCount();
            if(!(_activeContentIndex - 1 >= 0 || count < 0))    return false;
            animated = typeof(animated) === 'boolean' ? animated : true;
            this.set(_activeContentIndex - 1, animated);
        };

        this.reload = function(animated, index) {
            _contentControllers = {}
            this.setState({contentControllers: []});
            index = typeof(index) === 'number' ? index : 0;
            this.set(index, animated, true);
        };

        this.update = function(index, animated)  {
            let dataSource = _dataSource;
            if(!dataSource) return;

            let count = dataSource.mediaViewerContentCount();
            if(count >= 0 && (index < 0 || index >= count)) return;

            if(!_contentControllers[index.toString()])  return;

            let progress = _contentControllers[index.toString()].getProgress();

            delete _contentControllers[index.toString()];
            this.setState({contentControllers: Object.values(_contentControllers) });

            if(index === _activeContentIndex)   {
                this.set(index, animated, true, progress);
            } else {
                this.preload();
            }
        };

        this.loadContentController = function(index, progress, isPreload) {
            let dataSource = _dataSource;
            if(!dataSource) return false;
    
            let count = dataSource.mediaViewerContentCount();
            if(count >= 0 && (index < 0 || index >= count)) return false;
    
            if(!_contentControllers[index.toString()])  {
                let content = dataSource.mediaViewerContentForIndex(index);

                if(!content || (isPreload && content.isPreloadEnabled === false))   {
                    return false;
                }

                let controller = this.contentControllerForContent(content, progress);
                controller.props.key = index.toString();
                controller.index = index;
                if(typeof(progress) === 'number')   controller.setProgress(progress);
                _contentControllers[index.toString()] = controller;
                this.setState({contentControllers: Object.values(_contentControllers) });

            } else if(typeof(progress) === 'number')    {
                let contentController = _contentControllers[index.toString()];
                contentController.setProgress(progress);
            }

            return true;
        };

        this.applyContentController = function(index, animated, force) {
            let isIndexChanged = index !== _activeContentIndex;
            if(!(isIndexChanged || force)) return;
            let shift = index < _activeContentIndex ? -1 : 1;

            let activeContentController = _contentControllers[_activeContentIndex.toString()];
            if(activeContentController) {
                activeContentController.clearCallbacks();
                activeContentController.pause();
            }

            _activeContentIndex = index;

            let readyState = OEMediaViewerReadyState.noMeta;
            let playbackState = OEMediaViewerPlaybackState.pause;
            let duration = 0;
            let time = 0;
            let progress = 0;
            let contentSize = _contentSize;

            activeContentController = _contentControllers[_activeContentIndex.toString()];
            if(activeContentController) {

                if(activeContentController.content) {
                    if(activeContentController.content.autoPlayback)    activeContentController.play();
                }

                activeContentController.setCallbacks(this.onReadyStateChanged, this.onPlaybackStateChanged, this.onDurationChanged, this.onCurrentTimeChanged, this.onProgressChanged, this.onEnded, this.onContentSizeChanged, this.onGoToItem, this.onShowMedia);
                readyState = activeContentController.getReadyState();
                playbackState = activeContentController.getPlaybackState();
                duration = activeContentController.getDuration();
                time = activeContentController.getCurrentTime();
                progress = activeContentController.getProgress();
                contentSize = activeContentController.getContentSize();
            }

            animated = typeof(animated) === 'boolean' ? animated : false;
            this.setState({
                activeContentIndex: _activeContentIndex,
                activeContentController: _contentControllers[index.toString()],
                animated: animated,
                shift: shift,
                readyState: readyState,
                playbackState: playbackState,
                duration: duration,
                time: time
            });
        
            if(!animated)   {
                this.updateCache();
            }

            if(isIndexChanged)  {
                this.props.onMediaItemChanged(_activeContentIndex);
            }

            this.props.onReadyStateChanged(readyState);
            this.props.onPlaybackStateChanged(playbackState);
            this.props.onDurationChanged(duration);
            this.props.onTimeChanged(time);
            this.props.onProgressChanged(progress);

            this.onContentSizeChanged_(contentSize);
        };

        this.onAnimCompleted = function()  {
            this.updateCache();
        };

        this.contentControllerForContent = function(content, progress) {
            progress = typeof(progress) === 'number' ? progress : 0;
            let props = {src: content.path, loop: content.loop, backgroundColor: content.backgroundColor, size: content.size};

            switch(content.type)    {
            case OEMediaViewerContentType.image:
                return new ContentControllerAdapter(OEMediaViewerImageContentController, props, content, progress);
            case OEMediaViewerContentType.video:
                return new ContentControllerAdapter(OEMediaViewerVideoContentController, props, content, progress);
            case OEMediaViewerContentType.web:
                return new ContentControllerAdapter(OEMediaViewerHtmlContentController, props, content, progress);
            case OEMediaViewerContentType.pdf:
                return new ContentControllerAdapter(OEMediaViewerPDFContentController, props, content, progress);
            default:
                return new ContentControllerAdapter(OEMediaViewerContentController, props, content, progress);
            }
        };

        this.onContentSizeChanged_ = function(contentSize) {
            if(!contentSize || OEToolbox.shallowEqual(contentSize, _contentSize))   return;
            _contentSize = contentSize;
            this.updateContentRect();
            this.props.onContentSizeChanged(contentSize);
        };

        this.updateContentRect = function() {
            if(!this.sizeObserver) return;

            if(!_contentSize)   {
                if(_contentRect)    {
                    _contentRect = null;
                    this.setState({contentRect: null});
                }
                return;
            }

            let size = this.sizeObserver.getSize();
            let contentRect = {x: 0, y: 0, w: size.w, h: size.h, mode: _contentSize.mode};

            if(size.h == 0 || _contentSize.h == 0)  {
                if(_contentSize.mode === OEMediaViewerContentSizeMode.fill) {
                    contentRect.top = contentRect.y;
                    contentRect.left = contentRect.x;
                    contentRect.bottom = size.h - (contentRect.y + contentRect.h);
                    contentRect.right = size.w - (contentRect.x + contentRect.w);

                    if(!OEToolbox.shallowEqual(_contentRect, contentRect)) {
                        _contentRect = contentRect;
                        this.setState({contentRect: contentRect});
                    }
                }  
                return;
            }

            let aspect = size.w / size.h;
            let contentAspect = _contentSize.w / _contentSize.h;

            if(contentAspect > aspect)  {
                contentRect.h = contentRect.w / contentAspect;
                contentRect.y = 0.5 * (size.h - contentRect.h);
            } else {
                contentRect.w = contentRect.h * contentAspect;
                contentRect.x = 0.5 * (size.w - contentRect.w);
            }

            contentRect.top = contentRect.y;
            contentRect.left = contentRect.x;
            contentRect.bottom = size.h - (contentRect.y + contentRect.h);
            contentRect.right = size.w - (contentRect.x + contentRect.w);
            
            if(!OEToolbox.shallowEqual(_contentRect, contentRect))  {
                _contentRect = contentRect;
                this.setState({contentRect: contentRect});
            }
        };

        this.onResize = function(sender, size) {
            this.sizeObserver = sender;
            this.updateContentRect();
        }.bind(this);

        this.mouseEventRoutingOptions = {hideElement: null};

        this.onOverlayElementRef = function(ref) {
            this.mouseEventRoutingOptions.hideElement = ref;
        }.bind(this);

        this.onMouseEventTargetRef = function(ref) {
            if(this.mouseEventTargetRef === ref)    return;
            this.mouseEventTargetRef = ref;
            //OEEventHelper.routeMouseEvents(this.mouseEventTargetRef, this.mouseEventRoutingOptions);
        }.bind(this);

        this.onOverlayRef = function(ref)   {
            this.overlayRef = ref;
        }.bind(this);

        this.onMouseMove = function(event) {
            if(this.overlayRef) {
                this.overlayRef.resetShowCycle({x: event.clientX, y: event.clientY});
            }
        }.bind(this);

        this.onSwipeLeft = function(event) {
            if(this.state.activeContentController) {
                if(!this.state.activeContentController.canSwipe()) return;
            }

            if(this.overlayRef) {
                this.overlayRef.onSwipeLeft(event);
            }
        }.bind(this);

        this.onSwipeRight = function(event) {
            if(this.state.activeContentController) {
                if(!this.state.activeContentController.canSwipe()) return;
            }

            if(this.overlayRef) {
                this.overlayRef.onSwipeRight(event);
            }
        }.bind(this);

        this.onTap = function(event) {
            if(this.overlayRef) {
                this.overlayRef.resetShowCycle(event.center);
            }
        }.bind(this);

        this.onAnimCompleted = this.onAnimCompleted.bind(this);

        // overlay controller delegation
        this.playbackStateChange = this.playbackStateChange.bind(this);
        this.playbackStateChangeStop = this.playbackStateChangeStop.bind(this);
        this.progressChange = this.progressChange.bind(this);
        this.indexChange = this.indexChange.bind(this);
        this.shouldChangePrev = this.shouldChangePrev.bind(this);
        this.shouldChangeNext = this.shouldChangeNext.bind(this);
        this.onFullscreenBtnPressed = this.onFullscreenBtnPressed.bind(this);
        this.onCloseBtnPressed = this.onCloseBtnPressed.bind(this);

        // active content controller delegation
        this.onReadyStateChanged = this.onReadyStateChanged.bind(this);
        this.onPlaybackStateChanged = this.onPlaybackStateChanged.bind(this);
        this.onDurationChanged = this.onDurationChanged.bind(this);
        this.onCurrentTimeChanged = this.onCurrentTimeChanged.bind(this);
        this.onProgressChanged = this.onProgressChanged.bind(this);
        this.onEnded = this.onEnded.bind(this);
        this.onContentSizeChanged = this.onContentSizeChanged.bind(this);
        this.onGoToItem = this.onGoToItem.bind(this);
        this.onShowMedia = this.onShowMedia.bind(this);
    }

    render()    {
        let animated = this.state.animated;
        let enter = null;
        let leave = null;

        if(animated)    {
            let shift = this.state.shift;
            if(shift < 0)   {
                enter = { translateX: ['0%', '-100%'], zIndex:[1, 0] };
                leave = { translateX: ['100%', '0%'], zIndex:[0, 1] };
            } else if(shift > 0)   {
                enter = { translateX: ['0%', '100%'], zIndex:[1, 0] };
                leave = { translateX: ['-100%', '0%'], zIndex:[0, 1] };
            }
            /*
            enter = { opacity: 1 };
            leave = { opacity: 0 };
            */
        }

        let aContentController = this.state.activeContentController;
        let aContent = null;

        let mediaType = OEMediaViewerContentType.dummy;

        if(aContentController)  {
            aContent = [React.createElement(aContentController.element, Object.assign(aContentController.props, {contentRect: this.state.contentRect, fullscreen: this.props.fullscreen}))];
            mediaType = aContentController.content.type;
        }

        let cachedContent = this.state.contentControllers.filter(controller => controller.index !== this.state.activeContentIndex && controller.content.type !== OEMediaViewerContentType.video);
        cachedContent = cachedContent.map(controller => React.createElement(controller.element, controller.props));

        return (
            <div
                className={'media-viewer-window ' + this.props.className}
                style={{backgroundColor: this.props.backgroundColor}}
                onMouseMove={this.onMouseMove}
            >

                <OEResizeObserver onResize={this.onResize} />

                <div className="media-viewer-content-cache">
                    {cachedContent}
                </div>

                <Hammer
                    onSwipeLeft={this.onSwipeLeft}
                    onSwipeRight={this.onSwipeRight}
                    onTap={this.onTap}
                >
                    <div className="media-viewer-window-content-ui-layer">

                        <div className="media-viewer-window-content-container">
                            <VelocityTransitionGroup enter={{animation: enter, duration: 333}} leave={{animation: leave, duration: 333, complete: this.onAnimCompleted}}>
                                {aContent}
                            </VelocityTransitionGroup>
                        </div>

                        <OEMediaViewerOverlayController
                            activeContentIndex={this.state.activeContentIndex}
                            dataSource={this.state.dataSource}
                            contentRect={this.state.contentRect}
                            showCloseBtn={this.props.showCloseBtn}
                            showControlsWhenWindow={this.props.showControlsWhenWindow}
                            showControlsPermanentlyWhenPaused={this.props.showControlsPermanentlyWhenPaused}
                            contentAlignedWhenWindowed={this.props.controlsContentAlignedWhenWindowed}
                            showNextPrevButtons={this.props.showNextPrevButtons}
                            playbackStateChange={this.playbackStateChange}
                            playbackStateChangeStop={this.playbackStateChangeStop}
                            progressChange={this.progressChange}
                            indexChange={this.indexChange}
                            shouldChangePrev={this.shouldChangePrev}
                            shouldChangeNext={this.shouldChangeNext}
                            onFullscreenBtnPressed={this.onFullscreenBtnPressed}
                            onCloseBtnPressed={this.onCloseBtnPressed}
                            onProgressSliderStart={this.props.onProgressSliderStart}
                            onProgressSliderChanged={this.props.onProgressSliderChanged}
                            onProgressSliderEnd={this.props.onProgressSliderEnd}
                            mediaType={mediaType}
                            fullscreen={this.props.fullscreen}
                            readyState={this.state.readyState}
                            playbackState={this.state.playbackState}
                            duration={this.state.duration}
                            time={this.state.time}
                            ref={this.onOverlayRef}
                            elementRef={this.onOverlayElementRef}
                            mouseEventTargetRef={this.onMouseEventTargetRef}
                        />

                    </div>
                </Hammer>

            </div>
        );
    }

    // overlay controller delegation
    playbackStateChange(state)  {
        if(state === OEMediaViewerPlaybackState.pause)  {
            this.pause();
        } else {
            this.play();
        }
    }

    playbackStateChangeStop()   {
        this.stop();
    }

    progressChange(progress)    {
        this.setProgress(progress);
    }

    indexChange(change)  {
        this.set(this.getActiveContentIndex() + change, true);
    }

    shouldChangePrev()  {
        let dataSource = this.getDataSource();
        let count = dataSource ? dataSource.mediaViewerContentCount() : 0;
        if(count == 0 || count == 1 || (this.getActiveContentIndex() == 0 && count >= 0)) return false;
        return this.props.shouldChangePrev(this.getActiveContentIndex());
    }

    shouldChangeNext()  {
        let dataSource = this.getDataSource();
        let count = dataSource ? dataSource.mediaViewerContentCount() : 0;
        if(count == 0 || count == 1 || (this.getActiveContentIndex() + 1 >= count && count >= 0)) return false;
        return this.props.shouldChangeNext(this.getActiveContentIndex());
    }

    onFullscreenBtnPressed(fullscreen)    {
        this.props.onFullscreenBtnPressed(fullscreen);
    }

    onCloseBtnPressed() {
        this.props.onCloseBtnPressed();
    }

    // active content controller delegation
    onReadyStateChanged(controller, state)  {
        if(controller.index !== this.getActiveContentIndex())    return;
        this.setState({readyState: state});
        this.props.onReadyStateChanged(state);
    }

    onPlaybackStateChanged(controller, state)   {
        if(controller.index !== this.getActiveContentIndex())    return;
        this.setState({playbackState: state});
        this.props.onPlaybackStateChanged(state);
    }

    onDurationChanged(controller, duration)  {
        if(controller.index !== this.getActiveContentIndex())    return;
        this.setState({duration: duration});
        this.props.onDurationChanged(duration);
    }

    onCurrentTimeChanged(controller, currentTime)   {
        if(controller.index !== this.getActiveContentIndex())    return;
        this.setState({time: currentTime});
        this.props.onTimeChanged(currentTime);
    }

    onProgressChanged(controller, progress) {
        if(controller.index !== this.getActiveContentIndex())    return;
        this.props.onProgressChanged(progress);
    }

    onEnded(controller) {
        if(controller.index !== this.getActiveContentIndex())    return;
        this.props.onEnded();
    }

    onContentSizeChanged(controller, contentSize) {
        if(controller.index !== this.getActiveContentIndex())    return;
        this.onContentSizeChanged_(contentSize);
    }

    onGoToItem(controller, name)    {
        this.props.onGoToItem(name);
    }

    onShowMedia(controller, path)   {
        this.props.onShowMedia(path);
    }
}

OEMediaViewerWindowController.defaultProps = {
    className: '',
    preloadWidth: 2,
    isCaching: true,
    showCloseBtn: false,
    showControlsWhenWindow: false,
    showControlsPermanentlyWhenPaused: true,
    controlsContentAlignedWhenWindowed: true,
    showNextPrevButtons: true,
    fullscreen: false,
    onMediaItemChanged: (index) => {},
    onReadyStateChanged: (state) => {},
    onPlaybackStateChanged: (state) => {},
    onTimeChanged: (time) => {},
    onDurationChanged: (duration) => {},
    onProgressChanged: (progress) => {},
    onEnded: () => {},
    onContentSizeChanged: (contentSize) => {},
    shouldChangePrev: (index) => { return true; },
    shouldChangeNext: (index) => { return true; },
    onGoToItem: (name) => {},
    onShowMedia: (path) => {},
    onFullscreenBtnPressed: (fullscreen) => { },
    onCloseBtnPressed: () => {},
    onProgressSliderStart: (value) => {},
    onProgressSliderChanged: (value) => {},
    onProgressSliderEnd: (value) => {},
    backgroundColor: ''
};

OEMediaViewerWindowController.propTypes = {
    className: PropTypes.string,
    preloadWidth: PropTypes.number,
    isCaching: PropTypes.bool,
    showCloseBtn: PropTypes.bool,
    showControlsWhenWindow: PropTypes.bool,
    showControlsPermanentlyWhenPaused: PropTypes.bool,
    controlsContentAlignedWhenWindowed: PropTypes.bool,
    showNextPrevButtons: PropTypes.bool,
    fullscreen: PropTypes.bool,
    onMediaItemChanged: PropTypes.func,
    onReadyStateChanged: PropTypes.func,
    onPlaybackStateChanged: PropTypes.func,
    onTimeChanged: PropTypes.func,
    onDurationChanged: PropTypes.func,
    onProgressChanged: PropTypes.func,
    onEnded: PropTypes.func,
    onContentSizeChanged: PropTypes.func,
    shouldChangePrev: PropTypes.func,
    shouldChangeNext: PropTypes.func,
    onGoToItem: PropTypes.func,
    onShowMedia: PropTypes.func,
    onFullscreenBtnPressed: PropTypes.func,
    onCloseBtnPressed: PropTypes.func,
    onProgressSliderStart: PropTypes.func,
    onProgressSliderChanged: PropTypes.func,
    onProgressSliderEnd: PropTypes.func,
    backgroundColor: PropTypes.string
};